import type { UnitType } from "@repo/supabase/database-types"
import {
  BUSINESS_ENTITY_SYSTEM_ROLE_ID,
  CLIENT_SYSTEM_ROLE_ID,
  INTERNAL_SYSTEM_ROLE_ID,
  RESOURCE_SYSTEM_ROLE_ID,
  SUPER_ADMIN_ROLE_ID,
} from "./constants"
import type {
  AppRole,
  UnitRolePermissions,
  UnitRolePermissionsMap,
} from "./types"

export function createRolePermissionMethods(
  appRole: AppRole | null,
  unitRolePermissions: UnitRolePermissionsMap,
) {
  return new UnitRolePermissionMethods(appRole, unitRolePermissions)
}

class UnitRolePermissionMethods {
  #unitRolePermissions: UnitRolePermissions[]

  constructor(
    readonly appRole: AppRole | null,
    readonly unitRolePermissionsMap: UnitRolePermissionsMap,
  ) {
    this.#unitRolePermissions = Object.values(unitRolePermissionsMap)
  }

  getAppRole() {
    return this.appRole
  }

  getIsSuperAdmin() {
    return this.appRole?.id === SUPER_ADMIN_ROLE_ID
  }

  getIsInternalSystemUser() {
    return this.appRole?.id === INTERNAL_SYSTEM_ROLE_ID
  }

  getIsBusinessEntitySystemUser() {
    return this.appRole?.id === BUSINESS_ENTITY_SYSTEM_ROLE_ID
  }

  getIsResourceSystemUser() {
    return this.appRole?.id === RESOURCE_SYSTEM_ROLE_ID
  }

  getIsClientSystemUser() {
    return this.appRole?.id === CLIENT_SYSTEM_ROLE_ID
  }

  /**
   * Get all the unit IDs that the user has access to (explicitly or inherited)
   * @returns An array of unit IDs
   */
  getUnitIds() {
    return this.#unitRolePermissions.map((permission) => permission.unitId)
  }

  /**
   * Get all the unit IDs that the user has access to explicitly (not inherited)
   * @returns An array of unit IDs
   */
  getExplicitUnitIds() {
    return this.#unitRolePermissions
      .filter((permission) => !permission.isInherited)
      .map((permission) => permission.unitId)
  }

  getUnitRolePermissions() {
    return this.#unitRolePermissions
  }

  /**
   * Get the unit permissions for the user's system membership
   */
  getSystemUnitPermissions() {
    return this.#unitRolePermissions.find(
      (permission) => permission.unitType === "system",
    )
  }

  /**
   * Get the unit permissions for the user's internal unit
   */
  getInternalUnitPermissions() {
    return this.#unitRolePermissions.find(
      (permission) => permission.unitType === "internal",
    )
  }

  /**
   * Get the unit permissions for the given unit ID
   */
  getUnitPermissions(unitId: string) {
    return this.#unitRolePermissions.find(
      (permission) => permission.unitId === unitId,
    )
  }

  /**
   * Get the all the unit permissions for the given unit type
   */
  getUnitTypePermissions(unitType: UnitType) {
    return this.#unitRolePermissions.filter(
      (permission) => permission.unitType === unitType,
    )
  }

  /**
   * Get the unit permissions for the given unit slug and type
   */
  getUnitPermissionsBySlug(unitSlug: string, unitType: UnitType) {
    return this.#unitRolePermissions.find(
      (permission) =>
        permission.unitSlug === unitSlug && permission.unitType === unitType,
    )
  }

  /**
   * Get the unit ID from the unit slug and type
   */
  getUnitIdFromSlug(unitSlug: string, unitType: UnitType) {
    return this.getUnitPermissionsBySlug(unitSlug, unitType)?.unitId
  }

  /**
   * Does the current user have the given permission for the given unit?
   */
  hasUnitPermission(unitId: string, permission: string) {
    if (this.getIsSuperAdmin()) return true

    const unitPermissions = this.getUnitPermissions(unitId)

    if (!unitPermissions) return false

    return unitPermissions.permissions.includes(permission)
  }

  /**
   * Does the current user have the given permission for any unit of the given type?
   */
  hasUnitTypePermission(unitType: UnitType, permission: string) {
    if (this.getIsSuperAdmin()) return true

    const unitTypePermissions = this.getUnitTypePermissions(unitType)

    if (!unitTypePermissions) return false

    return unitTypePermissions.some((unitPermissions) =>
      unitPermissions.permissions.includes(permission),
    )
  }

  doesRoleHaveAdminZoneAccess(roleId = this.getAppRole()?.id) {
    if (!roleId) {
      return false
    }

    return [SUPER_ADMIN_ROLE_ID, INTERNAL_SYSTEM_ROLE_ID].includes(roleId)
  }
}
